import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import { useNavigate } from 'react-router-dom';
import { IOrder } from '../../interfaces/studio';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmptyListMessage from '../common/EmptyListMessage';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { useCampaignStatusColors } from '../../hooks/useCampaignStatusColors';
import { Skeleton } from '@mui/material';
import { useDebounce } from 'use-debounce';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';

export default function PreviousOrdersTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(true);
	const [fetchedData, setFetchedData] = useState<IOrder[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [skeleton, setSkeleton] = useState(true);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewOrder = (orderId: string) => () => {
		navigate(`/studio/view/${orderId}`);
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const goToStudio = () => navigate('/studio');

	const properties: (keyof IOrder)[] = ['title', 'content_type', 'media', 'status'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<IOrder>[] = [
		{
			field: 'id',
			headerName: t('row'),
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			flex: 1,
			minWidth: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'media',
			headerName: t('platform'),
			width: 130,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'content_type',
			headerName: t('content_type'),
			width: 130,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('order_date'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'finished_at',
			headerName: t('deliver_date'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				if (params.value) {
					return new Date(params.value).toLocaleDateString('fa-IR');
				} else {
					return '____';
				}
			},
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 160,
			display: 'flex',
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			align: 'center',
			renderCell: params => {
				const bgColorClass = useCampaignStatusColors(params.value);
				return <div className={`flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{t(params.value)}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 120,
			headerAlign: 'center',
			headerClassName: headerClassNames,
			align: 'center',
			renderCell: params => {
				const editableData = ['rejected', 'payment_pend', 'pending'].includes(params?.row?.status ?? '');
				return (
					<>
						<IconButton onClick={handleViewOrder(params.row.id.toString())} className={editableData ? '' : 'invisible'}>
							<BorderColorOutlinedIcon className={'text-text-secondary'} />
						</IconButton>

						<IconButton onClick={handleViewOrder(params.row.id.toString())}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		setLoading(true);
		axios<IResponse<IOrder[]>>({
			url: '/studio/requests',
			params: {
				page: page,
				limit: pageSize,
				search: search,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setSkeleton(false);
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return skeleton ? (
		<div className={'flex w-full flex-col gap-6'}>
			{new Array(10).fill(0).map(() => (
				<Skeleton />
			))}
		</div>
	) : filteredRows.length === 0 && !searchTerm && !loading ? (
		<EmptyListMessage buttonLabel={t('view_packages')} message={t('empty_order_list_message')} onClick={goToStudio} />
	) : (
		<BaranDataGrid<IOrder> pagination={pagination} loading={loading} rows={filteredRows} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={goToStudio} actionButtonLable={t('new_order')} searchableFields={properties} />
	);
}
